import { useCallback, useState } from "react";
import { ButtonProps } from "@literati/public-ui-shared-react";

import { FormAction } from "components/authenticate/authTypes";
import { FORM_ID } from "components/authenticate/AuthForm";
import { SubmitStateChangeEvent } from "components/authenticate/AuthFormSections";

const BUTTON_TEXT: Record<FormAction, string> = {
  createDummyAccount: "Continue",
  createAccount: "Create Account",
  login: "Log In",
};

export interface UseAuthFormArgs {
  initialFormAction: FormAction;
  submitType?: "alwaysEnabled" | "stateful";
}

/**
 * @note - A UX decision was made to keep form submit buttons enabled by default. (Feb '24)
 */
const useAuthForm = ({
  initialFormAction,
  submitType = "alwaysEnabled",
}: UseAuthFormArgs) => {
  const [formAction, setFormAction] = useState<FormAction>(initialFormAction);
  const setFormActionCallback = useCallback(
    (formAction: FormAction) => {
      setFormAction(formAction);
    },
    [setFormAction]
  );

  const [submitButtonState, setSubmitButtonState] =
    useState<SubmitStateChangeEvent>({
      disableSubmit: true,
      isSubmitting: false,
    });

  const setSubmitButtonStateCallback = useCallback(
    ({ disableSubmit, isSubmitting }: SubmitStateChangeEvent) => {
      setSubmitButtonState((prev) => ({
        ...prev,
        disableSubmit,
        isSubmitting,
      }));
    },
    [setSubmitButtonState]
  );

  const submitButtonProps: ButtonProps = {
    children: BUTTON_TEXT[formAction],
    disabled:
      submitType === "alwaysEnabled" ? false : submitButtonState.disableSubmit,
    loading: submitButtonState.isSubmitting,
    type: "submit",
    form: FORM_ID,
  };

  return {
    authFormProps: {
      onSubmitStateChange: setSubmitButtonStateCallback,
      onToggleFormAction: setFormActionCallback,
    },
    formAction,
    isDummyAccountForm: formAction === "createDummyAccount",
    isCreateAccountForm: formAction === "createAccount",
    isLoginForm: formAction === "login",
    submitButtonProps,
  };
};

export default useAuthForm;
